import { useEffect, useState } from 'react';
import { selectCurrentUser, useAppSelector } from 'store';
import { BlogPost, BlogPostSimple, CurrentUser, MenuResponse, OgData } from 'types';
import { Banner } from 'components/Banner';
import { Snackbar, SnackbarProps } from 'components/Snackbar';
import { SubscribeBlock } from './SubscribeBlock';
import { CustomerPageWrapper } from 'containers/CustomerPageWrapper';
import { DownloadAppBanner } from 'components/DownloadAppBanner';
import { isEmpty } from 'utils/toggleParams';
import { getDataFromLocalStore } from 'utils/localStoreTools';
import { RelatedArticles } from './RelatedArticles';

import styles from './BlogPage.module.scss';
import { getPageH1Title } from '../../utils/seoFormatters';

export type BlogPostPageProps = {
  blogPost: BlogPost;
  menuData: MenuResponse;
  ogData?: OgData[];
  user?: CurrentUser;
  relatedPosts: Array<BlogPostSimple>;
};

export const BlogPostPage = ({ blogPost, menuData, ogData, user, relatedPosts }: BlogPostPageProps) => {
  const currentUser = useAppSelector(selectCurrentUser);

  const [isSubscribed, setSubscribed] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<SnackbarProps | null>(null);

  const isAuthenticated = !isEmpty(currentUser);

  useEffect(() => {
    setSubscribed(isAuthenticated && getDataFromLocalStore('subscribedToNewsletter')?.includes(blogPost.activity.slug));
  }, [isAuthenticated, blogPost]);

  return (
    <CustomerPageWrapper
      headTitle={blogPost.title}
      menuData={menuData}
      ogData={ogData}
      user={user}
      hiddenComponentProps={{ title: getPageH1Title('blogPost', { title: blogPost.title }) }}
    >
      <div>
        <Banner src={blogPost?.image} title={blogPost.title} subtitle={blogPost.subtitle} centered />

        <div className={styles.postContainer}>
          <h2 className={styles.postTitle}>{blogPost.title}</h2>
          <div className={styles.postContent} dangerouslySetInnerHTML={{ __html: blogPost.body }} />
        </div>

        {!isSubscribed && (
          <SubscribeBlock
            blogPost={blogPost}
            isAuthenticated={isAuthenticated}
            user={currentUser}
            setSnackbarMessage={setSnackbarMessage}
            setSubscribed={setSubscribed}
          />
        )}

        <DownloadAppBanner />

        {relatedPosts.length > 0 && <RelatedArticles articles={relatedPosts} />}

        {snackbarMessage && <Snackbar {...snackbarMessage} />}
      </div>
    </CustomerPageWrapper>
  );
};
